import { useEffect, useState } from "react";
import { NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useUserStore } from "./store/user";
import { useTheme } from "./hooks/useTheme";
import Sheet from "./components/Sheet";
import Splash from "./components/Splash";
import WebLogin from "./components/WebLogin";
import { isTelegram } from "./lib/platform";
import { haptic } from "./lib/tg";
import { useBackButton } from "./hooks/useTelegramUI";
import HomePage from "./pages/HomePage";
import CatalogPage from "./pages/CatalogPage";
import LikesPage from "./pages/LikesPage";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import ProductPage from "./pages/ProductPage";
import ProfilePage from "./pages/ProfilePage";
import ManagerPage from "./pages/ManagerPage";
import OwnerPage from "./pages/OwnerPage";
import AdminPage from "./pages/AdminPage";
import type { Role } from "./types";

const ROLE_LABELS: Record<Role, string> = {
  client:  "Клиент",
  manager: "Менеджер",
  owner:   "Владелец",
  admin:   "Админ",
};

const SWITCH_ROLES: Role[] = ["client", "manager", "owner", "admin"];

interface RoleSwitcherProps {
  open: boolean;
  onClose: () => void;
}

function RoleSwitcher({ open, onClose }: RoleSwitcherProps) {
  const { viewAs, setViewAs } = useUserStore();

  return (
    <Sheet open={open} title="Просматривать как…" onClose={onClose}>
      {SWITCH_ROLES.map((r) => (
        <button
          key={r}
          onClick={() => { haptic.select(); setViewAs(r); onClose(); }}
          style={{
            display: "block", width: "100%",
            textAlign: "left",
            padding: "13px 16px",
            marginBottom: 8,
            borderRadius: 12,
            border: "none",
            fontSize: 15,
            fontWeight: viewAs === r ? 700 : 400,
            background: viewAs === r ? "var(--button)" : "var(--secondary-bg)",
            color: viewAs === r ? "var(--button-text)" : "var(--text)",
            cursor: "pointer",
          }}
        >
          {ROLE_LABELS[r]}{viewAs === r && " ✓"}
        </button>
      ))}
    </Sheet>
  );
}

export default function App() {
  const { user, loading, error, load, viewAs } = useUserStore();
  useTheme(); // инициализирует тему из localStorage при старте
  const [roleOpen, setRoleOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // «Назад» в шапке клиента — только там, где нет своей вкладки в навигации:
  // на самих вкладках системная стрелка уводила бы в непредсказуемое место.
  const TAB_PATHS = ["/", "/catalog", "/likes", "/profile"];
  useBackButton(() => navigate(-1), !TAB_PATHS.includes(pathname));

  // Запущены ли мы внутри Telegram Mini App (есть initData) или в браузере (сайт).
  const inTelegram = isTelegram();

  // Грузим пользователя на обеих платформах: в TG — по initData, на сайте — по cookie.
  useEffect(() => { load(); }, [load]);

  // Диплинки: t.me/<bot>?startapp=product_<id> открывает товар,
  // ?startapp=order_<id> — документ по заказу (чек или талон брони), на который
  // ведут уведомления бота о выдаче и об истекающей броне.
  useEffect(() => {
    const sp = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    if (!sp) return;
    const product = /^product[_-](\d+)$/.exec(sp);
    if (product) { navigate(`/product/${product[1]}`); return; }
    const order = /^order[_-](\d+)$/.exec(sp);
    if (order) navigate(`/profile?order=${order[1]}`);
  }, [navigate]);

  if (loading) {
    return <Splash error={error} />;
  }
  if (!user) {
    // В Telegram нет юзера = реальная ошибка авторизации (показываем сплэш с ошибкой).
    // На сайте нет сессии = показываем экран входа (демо-вход / Telegram Login).
    return inTelegram ? <Splash error={error} /> : <WebLogin onDone={load} />;
  }

  const effectiveRole = viewAs ?? user.role;
  const isManager = effectiveRole === "manager" || effectiveRole === "owner" || effectiveRole === "admin";
  const isOwner   = effectiveRole === "owner" || effectiveRole === "admin";
  // Управление пользователями: по effectiveRole (viewAs влияет)
  const canManageUsers = effectiveRole === "owner" || effectiveRole === "admin";
  const isAdmin   = user.role === "admin";

  return (
    <>
      <RoleSwitcher open={roleOpen} onClose={() => setRoleOpen(false)} />

      {/* Десктоп-шапка (на мобильном скрыта, там нижняя навигация). */}
      <header className="site-header torn-bottom">
        <NavLink to="/" className="site-header__brand">Storosilov Vintage</NavLink>
        <nav className="site-header__nav">
          <NavLink to="/" end>Главная</NavLink>
          <NavLink to="/catalog">Каталог</NavLink>
          <NavLink to="/likes">Избранное</NavLink>
          <NavLink to="/profile">Профиль</NavLink>
          {isManager      && <NavLink to="/manager">Менеджер</NavLink>}
          {isOwner        && <NavLink to="/owner">Аналитика</NavLink>}
          {canManageUsers && <NavLink to="/admin">Польз.</NavLink>}
        </nav>
      </header>

      <Routes>
        {/* Витрина на корне, каталог — отдельным адресом: приложение открывается
            рассказом о магазине, а не сразу базой товаров. */}
        <Route path="/" element={<HomePage />} />
        <Route path="/catalog" element={<CatalogPage />} />
        <Route path="/likes" element={<LikesPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/product/:id" element={<ProductPage />} />
        <Route path="/profile" element={
          <ProfilePage
            onOpenRoleSwitcher={isAdmin ? () => setRoleOpen(true) : undefined}
            viewAsLabel={isAdmin && viewAs ? ROLE_LABELS[viewAs] : undefined}
            isStaff={isManager}
            canManageUsers={canManageUsers}
          />
        } />

        {isManager      && <Route path="/manager/*" element={<ManagerPage />} />}
        {isOwner        && <Route path="/owner/*"   element={<OwnerPage />} />}
        {canManageUsers && <Route path="/admin"     element={<AdminPage />} />}
      </Routes>

      {/* Рваный верх: на мобильном шапки нет, и без этого рваный край языку
          негде было бы прозвучать — панель отрывается от листа. */}
      {/* Нижняя навигация: держим не больше пяти пунктов — шестой на узком
          экране схлопывает подписи в кашу. Поэтому «Избранное» показываем только
          клиенту (у сотрудников его место занимают рабочие разделы), а
          «Пользователи» живут строкой в профиле, а не вкладкой. */}
      <nav className="bottom-nav torn-top">
        <NavLink to="/" end>Главная</NavLink>
        <NavLink to="/catalog">Каталог</NavLink>
        {!isManager && <NavLink to="/likes">♥ Избранное</NavLink>}
        {isManager  && <NavLink to="/manager">Менеджер</NavLink>}
        {isOwner    && <NavLink to="/owner">Аналитика</NavLink>}
        <NavLink to="/profile">Профиль</NavLink>
      </nav>
    </>
  );
}
